import MomentUtils from '@date-io/moment';
// eslint-disable-next-line import/no-unresolved
import { DateType } from '@date-io/type';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import moment from 'moment';
import React, { ReactNode, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Value } from '@/model/common';
import { noop } from '@/utils/functions';
import { Func } from '@/utils/ts';

interface DateFieldInputProps {
  'data-test'?: string;
  className?: string;
  value?: Value<Date>;
  onChange: Func<[Date | undefined]>;
  onError?: Func<[boolean]>;
  onFocus?: Func;
  onBlur?: Func;
  messages?: {
    invalid?: ReactNode;
    max?: ReactNode;
    min?: ReactNode;
    placeholder?: string;
  };
  minDate?: Date;
  maxDate?: Date;
  disables?: {
    future?: boolean;
    past?: boolean;
  };
}

const DateFieldInput: React.FC<DateFieldInputProps> = ({
  value,
  onChange,
  messages,
  disables,
  minDate,
  maxDate,
  className,
  onFocus = noop,
  onBlur = noop,
  onError = noop,
  'data-test': dataTest,
}) => {
  const [isFocussed, setFocussed] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const doStoreError = useCallback(
    async (isError: boolean) => {
      setHasError(isError);
      await onError(isError);
    },
    [onError],
  );
  const doFocus = useCallback(async () => {
    setFocussed(true);
    await onFocus();
  }, [onFocus]);
  const doBlur = useCallback(async () => {
    setFocussed(false);
    await onBlur();
  }, [onBlur]);
  return (
    <MuiPickersUtilsProvider utils={MomentUtils} libInstance={moment}>
      <KeyboardDatePicker
        data-test={dataTest && `${dataTest}-picker`}
        className={className}
        maxDate={maxDate}
        minDate={minDate}
        inputVariant="filled"
        InputLabelProps={{
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          'data-test': dataTest && `${dataTest}-label`,
        }}
        InputProps={{
          disableUnderline: true,
          inputProps: { inputMode: 'numeric' },
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          'data-test': dataTest && `${dataTest}-input`,
        }}
        onFocus={doFocus}
        onBlur={doBlur}
        onError={(error) => doStoreError(!!error)}
        error={!isFocussed && (!!value?.hasError || hasError)}
        {...(isFocussed ? { helperText: undefined } : {})}
        clearLabel={
          <span data-test={dataTest && `${dataTest}-clear`}>
            <FormattedMessage id="common.components.date.buttons.clear" />
          </span>
        }
        okLabel={
          <span data-test={dataTest && `${dataTest}-ok`}>
            <FormattedMessage id="common.components.date.buttons.ok" />
          </span>
        }
        cancelLabel={
          <span data-test={dataTest && `${dataTest}-cancel`}>
            <FormattedMessage id="common.components.date.buttons.cancel" />
          </span>
        }
        invalidDateMessage={
          messages?.invalid || (
            <span data-test={dataTest && `${dataTest}-invalid`}>
              <FormattedMessage id="common.components.date.errors.invalid" />
            </span>
          )
        }
        maxDateMessage={messages?.max || <FormattedMessage id="common.components.date.errors.max" />}
        minDateMessage={messages?.min || <FormattedMessage id="common.components.date.errors.min" />}
        clearable
        disableFuture={!!disables?.future}
        disablePast={!!disables?.past}
        variant="dialog"
        format="DD.MM.yyyy"
        value={value?.value || null}
        onChange={(v: DateType | null) => onChange(v ? v.toDate() : undefined)}
        label={messages?.placeholder}
      />
    </MuiPickersUtilsProvider>
  );
};

export default DateFieldInput;
